const SESSION_COOKIE_NAME = 'ept_session';
const LEGACY_AUTH_COOKIE_NAME = 'ept_auth';
const EPT_DOMAIN = '.ept.ai';
const SESSION_MAX_AGE_SECONDS = 3600;

/**
 * Whether the hostname should receive a shared `.ept.ai` cookie.
 *
 * @example
 * _shouldUseSharedDomain('app.ept.ai') // true
 */
function _shouldUseSharedDomain(hostname: string): boolean {
  return hostname.endsWith('.ept.ai');
}

/**
 * Sets a cross-domain session presence cookie for www.ept.ai.
 * Stores only a non-sensitive flag (not the JWT) so CloudFront can gate routes
 * and the marketing header can show the signed-in state on every page.
 *
 * @example
 * setEptSessionCookie()
 */
export function setEptSessionCookie(): void {
  try {
    const hostname = window.location.hostname;
    const domain = _shouldUseSharedDomain(hostname) ? `Domain=${EPT_DOMAIN}; ` : '';
    document.cookie = `${SESSION_COOKIE_NAME}=1; ${domain}Path=/; Secure; SameSite=Lax; Max-Age=${SESSION_MAX_AGE_SECONDS}`;
  } catch {
    // Must never affect main app auth flow.
  }
}

/**
 * Clears cross-domain Training Room cookies on logout.
 * Clears both Path=/ and Path=/training-room in case either was used historically.
 *
 * @example
 * clearEptSessionCookie()
 */
export function clearEptSessionCookie(): void {
  try {
    const hostname = window.location.hostname;
    const domain = _shouldUseSharedDomain(hostname) ? `Domain=${EPT_DOMAIN}; ` : '';
    const paths = ['/', '/training-room'];
    for (const path of paths) {
      document.cookie = `${SESSION_COOKIE_NAME}=; ${domain}Path=${path}; Secure; SameSite=Lax; Max-Age=0`;
      document.cookie = `${LEGACY_AUTH_COOKIE_NAME}=; ${domain}Path=${path}; Secure; SameSite=Lax; Max-Age=0`;
    }
  } catch {
    // Must never affect main app auth flow.
  }
}

/** @internal test helper */
export function buildEptSessionCookieValue(hostname: string): string {
  const domain = _shouldUseSharedDomain(hostname) ? `Domain=${EPT_DOMAIN}; ` : '';
  return `${SESSION_COOKIE_NAME}=1; ${domain}Path=/; Secure; SameSite=Lax; Max-Age=${SESSION_MAX_AGE_SECONDS}`;
}

/** @internal test helper */
export function buildClearEptSessionCookieValue(hostname: string): string {
  const domain = _shouldUseSharedDomain(hostname) ? `Domain=${EPT_DOMAIN}; ` : '';
  return `${SESSION_COOKIE_NAME}=; ${domain}Path=/; Secure; SameSite=Lax; Max-Age=0`;
}
